import type {} from '@mui/x-data-grid/themeAugmentation';
import { createTheme, ThemeOptions, alpha } from '@mui/material/styles';

/**
 * LMS Dashboard theme (MUI v5).
 *
 * Design tokens:
 *   - Primary:   Blue  #2563EB
 *   - Secondary: Sky blue     #0288d1
 *   - Background: Light blue-tinted  #EFF6FF
 *
 * Update ONLY via design token changes; do not patch individual screens.
 */

// ─── Palette tokens ──────────────────────────────────────────────────────────
const PRIMARY = '#2563EB';

const berryTokens = {
  primary: {
    lighter: "#EFF6FF", // blue-50
    light: "#93C5FD", // blue-300
    main: "#2563EB", // blue-600
    dark: "#1D4ED8", // blue-700
    darker: "#1E3A8A", // blue-900
    contrastText: "#FFFFFF",
  },
  secondary: {
    lighter: "#E3F2FD",
    light: "#90CAF9",
    main: "#0288D1",
    dark: "#0277BD",
    darker: "#01579B",
    contrastText: "#FFFFFF",
  },
  error: {
    lighter: "#FFEBEE",
    light: "#EF9A9A",
    main: "#D32F2F",
    dark: "#C62828",
    contrastText: "#FFFFFF",
  },
  warning: {
    lighter: "#FFF8E1",
    light: "#FFD54F",
    main: "#ED6C02",
    dark: "#E65100",
    contrastText: "#FFFFFF",
  },
  info: {
    lighter: "#E1F5FE",
    light: "#4FC3F7",
    main: "#0288D1",
    dark: "#0277BD",
    contrastText: "#FFFFFF",
  },
  success: {
    lighter: "#E8F5E9",
    light: "#81C784",
    main: "#2E7D32",
    dark: "#1B5E20",
    contrastText: "#FFFFFF",
  },
  grey: {
    0: "#FFFFFF",
    50: "#FAFAFA",
    100: "#F5F5F5",
    200: "#EEEEEE",
    300: "#E0E0E0",
    400: "#BDBDBD",
    500: "#9E9E9E",
    600: "#757575",
    700: "#616161",
    800: "#424242",
    900: "#212121",
  },
  background: {
    default: "#EFF6FF", // blue-tinted background derived from primary
    paper: "#FFFFFF",
  },
  text: {
    primary: "#1A2027",
    secondary: "#637381",
    disabled: "#919EAB",
  },
  divider: alpha("#E2E8F0", 0.24),
};

// ─── Theme options ────────────────────────────────────────────────────────────
const lmsThemeOptions: ThemeOptions = {
  palette: {
    mode: 'light',
    ...berryTokens,
  },
  typography: {
    fontFamily: '"Roboto", "Inter", "Helvetica", "Arial", sans-serif',
    h1: { fontSize: '2.125rem', fontWeight: 700, lineHeight: 1.235 },
    h2: { fontSize: '1.875rem', fontWeight: 700, lineHeight: 1.2 },
    h3: { fontSize: '1.5rem', fontWeight: 600, lineHeight: 1.167 },
    h4: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.235 },
    h5: { fontSize: '1.0625rem', fontWeight: 600, lineHeight: 1.334 },
    h6: { fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.6 },
    subtitle1: { fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.57 },
    subtitle2: { fontSize: '0.75rem', fontWeight: 500, lineHeight: 1.66 },
    body1: { fontSize: '0.875rem', lineHeight: 1.57 },
    body2: { fontSize: '0.8125rem', lineHeight: 1.66 },
    button: { textTransform: 'none', fontWeight: 600, fontSize: '0.875rem' },
    caption: { fontSize: '0.75rem', lineHeight: 1.66 },
    overline: { fontSize: '0.75rem', fontWeight: 600, letterSpacing: '1px', lineHeight: 2.5 },
  },
  shape: { borderRadius: 12 },
  spacing: 8,
  shadows: [
    'none',
    '0 1px 2px 0 rgba(145,158,171,0.20)',
    '0 2px 4px 0 rgba(145,158,171,0.20)',
    '0 4px 8px -2px rgba(145,158,171,0.24)',
    '0 6px 12px -4px rgba(145,158,171,0.24)',
    '0 8px 16px -4px rgba(145,158,171,0.24)',
    '0 12px 24px -4px rgba(145,158,171,0.24)',
    '0 16px 32px -4px rgba(145,158,171,0.24)',
    '0 20px 40px -4px rgba(145,158,171,0.24)',
    '0 24px 48px 0 rgba(145,158,171,0.24)',
    '0 2px 14px 0 rgba(32,40,45,0.08)',
    '0 2px 14px 0 rgba(32,40,45,0.08)',
    '0 2px 14px 0 rgba(32,40,45,0.08)',
    '0 2px 14px 0 rgba(32,40,45,0.08)',
    '0 2px 14px 0 rgba(32,40,45,0.08)',
    '0 2px 14px 0 rgba(32,40,45,0.08)',
    '0 2px 14px 0 rgba(32,40,45,0.08)',
    '0 2px 14px 0 rgba(32,40,45,0.08)',
    '0 2px 14px 0 rgba(32,40,45,0.08)',
    '0 2px 14px 0 rgba(32,40,45,0.08)',
    '0 2px 14px 0 rgba(32,40,45,0.08)',
    '0 2px 14px 0 rgba(32,40,45,0.08)',
    '0 2px 14px 0 rgba(32,40,45,0.08)',
    '0 2px 14px 0 rgba(32,40,45,0.08)',
    '0 2px 14px 0 rgba(32,40,45,0.08)',
  ],
  components: {
    // ── Button ──────────────────────────────────────────────
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 8,
          ':focus-visible': { outline: `2px solid ${PRIMARY}`, outlineOffset: 2 },
          '&.Mui-disabled': {
            pointerEvents: 'auto',
            cursor: 'not-allowed',
          },
        },
        sizeMedium: { padding: '8px 20px' },
        sizeLarge: { padding: '10px 24px', fontSize: '1rem' },
        sizeSmall: { padding: '4px 12px' },
        containedPrimary: {
          // blue shadow: rgb(37, 99, 235)
          boxShadow: '0 2px 10px 0 rgba(37,99,235,0.35)',
          '&:hover': { boxShadow: '0 6px 20px 0 rgba(37,99,235,0.40)' },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            pointerEvents: 'auto',
            cursor: 'not-allowed',
            opacity: 0.5,
          },
        },
      },
    },
    // ── Card ─────────────────────────────────────────────────
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 14px 0 rgba(32,40,45,0.08)',
          borderRadius: 16,
          border: 'none',
          '&:hover': { boxShadow: '0 4px 20px 0 rgba(32,40,45,0.14)' },
          transition: 'box-shadow 0.2s ease-in-out',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: { padding: 24, '&:last-child': { paddingBottom: 24 } },
      },
    },
    // ── AppBar ───────────────────────────────────────────────
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          borderBottom: '1px solid rgba(145, 158, 171, 0.24)',
          backgroundColor: '#FFFFFF',
          color: '#1A2027',
        },
      },
    },
    // ── Drawer ───────────────────────────────────────────────
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: 'none',
          boxShadow: '0 2px 14px 0 rgba(32,40,45,0.08)',
        },
      },
    },
    // ── DataGrid ─────────────────────────────────────────────
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: 'none',
          fontSize: '0.875rem',
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#EFF6FF',
            fontWeight: 600,
            fontSize: '0.75rem',
            color: '#637381',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          },
          '& .MuiDataGrid-cell': {
            borderBottom: '1px solid rgba(145, 158, 171, 0.08)',
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: alpha(PRIMARY, 0.04),
          },
        },
      },
    },
    // ── Table ────────────────────────────────────────────────
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 600,
          backgroundColor: '#EFF6FF',
          fontSize: '0.75rem',
          color: '#637381',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
        },
        root: { borderBottom: '1px solid rgba(145, 158, 171, 0.16)' },
      },
    },
    // ── TextField ────────────────────────────────────────────
    MuiTextField: {
      defaultProps: { variant: 'outlined', size: 'medium' },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: PRIMARY,
          },
        },
        notchedOutline: { borderColor: 'rgba(145, 158, 171, 0.32)' },
      },
    },
    // ── Chip ─────────────────────────────────────────────────
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 500, fontSize: '0.75rem' },
        colorPrimary: {
          backgroundColor: alpha(PRIMARY, 0.12),
          color: PRIMARY,
        },
      },
    },
    // ── Tooltip ──────────────────────────────────────────────
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#1A2027',
          fontSize: '0.75rem',
        },
      },
    },
    // ── ListItemButton ───────────────────────────────────────
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '&.Mui-selected': {
            backgroundColor: alpha(PRIMARY, 0.12),
            color: PRIMARY,
            '&:hover': { backgroundColor: alpha(PRIMARY, 0.16) },
          },
          '&:hover': { backgroundColor: alpha(PRIMARY, 0.06) },
        },
      },
    },
    // ── Avatar ───────────────────────────────────────────────
    MuiAvatar: {
      styleOverrides: {
        root: { fontWeight: 600, fontSize: '0.875rem' },
        colorDefault: {
          backgroundColor: alpha(PRIMARY, 0.12),
          color: PRIMARY,
        },
      },
    },
    // ── Paper ────────────────────────────────────────────────
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        rounded: { borderRadius: 12 },
      },
    },
    // ── Divider ──────────────────────────────────────────────
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: 'rgba(145, 158, 171, 0.16)' },
      },
    },
  },
};

export const lmsTheme = createTheme(lmsThemeOptions);
